<template>
  <div
    class="
      tw-fixed tw-mx-auto tw-z-toast tw-p-4
      two-drawer-layout__top-offset--after-secondary-subheader
      tw-left-1/2 tw-transform tw--translate-x-1/2
    "
  >
    <TransitionGroup name="toast-manager">
      <SfToast
        v-for="toast in effectiveToasts"
        :key="toast.id"
        class="tw-mb-3"
        :auto-dismiss-delay="toast.autoDismissAfter"
        :size="toast.size"
        :title-tooltip="toast.title"
        :message-tooltip="toast.message"
        :variant="toast.variant"
        @dismiss="() => toastStore.dismissToast(toast.id)"
      >
        <template #title>
          {{ toast.title }}
        </template>
        <template #message>
          {{ toast.message }}
        </template>
        <template #action>
          <component
            :is="action.componentType"
            v-for="action in toast?.actions ?? []"
            :key="action?.label || action?.icon"
            :icon="action?.icon"
            :variant="action?.variant"
            :seamless="!!action?.icon"
            :small="action?.small"
            @click="() => handleAction(toast.id, action)"
          >
            {{ action.label }}
          </component>
        </template>
      </SfToast>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import type { SilverfinEvents } from '@app/types'
import type { ToastPayloadAction } from '@app/shared/types/toast'

import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { MAX_VISIBLE_TOASTS } from '../constants'
import SfToast from '@app/shared/vue/components/sf-notification/sf-toast.vue'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import SfButtonText from '@app/shared/vue/components/sf-button/sf-button-text.vue'
import SfButtonIcon from '@app/shared/vue/components/sf-button/sf-button-icon.vue'
import { SfLog } from '@app/adapters/logger'

import { useToastStore } from '@app/shared/pinia/toast'

defineOptions({
  name: 'ToastManager',
})

const toastStore = useToastStore()
const { toastById, toastOrder } = storeToRefs(toastStore)

const effectiveToasts = computed(() =>
  toastOrder.value
    .map((id) => ({
      ...toastById.value[id],
      actions: (toastById.value[id]?.actions ?? []).map((action) => ({
        ...action,
        componentType: action.icon
          ? SfButtonIcon
          : action.buttonType === 'text'
          ? SfButtonText
          : SfButton,
      })),
    }))
    .splice(0, MAX_VISIBLE_TOASTS)
)

function handleAction(id: string, action: ToastPayloadAction): void {
  toastStore.dismissToast(id)

  if (action.url) {
    window.Turbolinks.visit(action.url)
  } else if (action.event) {
    const eventKey = Object.keys(action.event)[0] as keyof SilverfinEvents
    const payload = action.event[
      eventKey
    ] as SilverfinEvents[keyof SilverfinEvents]

    window.eventHub.$emit(eventKey, payload)
  } else {
    SfLog.error('No action defined for toast action', {
      error: new Error('No action defined for toast action'),
      extra: { action, ...window.Silverfin.jsonParams },
    })
  }
}
</script>
<style lang="scss" scoped>
.toast-manager {
  &-move,
  &-enter-active,
  &-leave-active {
    transition: all 0.3s ease;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateY(-30px);
  }

  &-leave-active {
    position: absolute;
  }
}
</style>
